import type * as Highcharts from 'highcharts';
import {
  tooltipFormat,
  type HighchartSeries,
  getHighchartColor,
  highChartsNegativeColorIndex,
  highChartsPositiveColorIndex,
} from 'components/technical/charts/HighChartsWrapper/Highchart.utils';
import HighChartsWrapper from 'components/technical/charts/HighChartsWrapper/HighChartsWrapper';
import { isNil } from 'lodash/fp';
import { Skeleton, Stack, Typography } from '@mui/joy';
import dayjs from 'dayjs';
import { tooltipFormatter, valueFormatter, type SupportedTwrFields } from './SubFundPerformanceLineChart';
import { Suspense } from 'react';
import { bignumber } from 'mathjs';
import { useFinalColorScheme } from 'technical/useFinalColorScheme.ts';
import type { ITimeWeightedReturnType } from '../../../../../scalars.ts';
import { logWarnOnce } from 'components/log.utils';

const calculateOptions = (categories: string[], field: SupportedTwrFields): Highcharts.Options => {
  return {
    exporting: {
      enabled: true,
    },
    chart: {
      type: 'bar',
    },
    ...tooltipFormat,
    xAxis: {
      categories,
      lineWidth: 0,
      tickInterval: Math.ceil(categories.length / 10), // Reduce steps dynamically
      min: 0,
      max: Math.min(categories.length - 1, categories.length + 1), // Auto-zoom on first n bars
    },
    yAxis: {
      title: {
        text: undefined,
      },
      labels: {
        formatter: tooltipFormatter[field],
      },
    },
    legend: {
      enabled: false,
      itemStyle: {
        color: 'inherit',
      },
    },
  };
};

const calculateChartData = ({
  colorScheme,
  data,
  categories,
  twrField,
}: {
  colorScheme: 'dark' | 'light';
  data: {
    subFund: {
      id: number;
      name: string;
    };
    twr: ITimeWeightedReturnType;
  }[];
  categories: string[];
  twrField: SupportedTwrFields;
}): HighchartSeries[] => {
  const seriesData = categories.map((subFundName, index) => {
    const dataItem = data.find((item) => item.subFund.name === subFundName);
    const twrValue = dataItem?.twr.return[twrField];

    const colorIndex = twrValue
      ? bignumber(twrValue).isNegative()
        ? highChartsNegativeColorIndex
        : highChartsPositiveColorIndex
      : highChartsNegativeColorIndex;
    return {
      x: index, // Ensures a unique position on x-axis
      y: twrValue ? bignumber(twrValue).abs().toNumber() : 0,
      color: getHighchartColor(colorScheme, colorIndex),
      textValue: valueFormatter[twrField](twrValue),
    };
  });

  return [
    {
      name: twrField,
      data: seriesData,
      type: 'column',
    },
  ];
};

export const SubFundLastDayBarChart = ({
  data,
  twrField,
  hideTitle,
  fullHeight,
}: {
  data:
    | {
        subFund: {
          id: number;
          name: string;
        };
        twr: ITimeWeightedReturnType;
      }[]
    | null;
  // for balance, we use a different type of chart - SubFundBalanceChart
  twrField: Exclude<SupportedTwrFields, 'balance'>;
  hideTitle?: boolean;
  fullHeight?: boolean;
}) => {
  const colorScheme = useFinalColorScheme();

  if (isNil(data) || data.length === 0) {
    logWarnOnce('SubFundLastDayBarChart can`t render because data is null or empty');
    return null;
  }

  // Create an array of subFund names with their TWR values
  const categoriesWithValues = data.map((item) => ({
    name: item.subFund.name,
    value: bignumber(item.twr.return[twrField] ?? 0).toNumber(),
  }));

  // Sort by value descending (biggest positive first)
  const sortedCategories = categoriesWithValues.sort((a, b) => b.value - a.value).map((item) => item.name); // Extract sorted names for the x-axis

  const dateSubTitle = dayjs(data[0].twr.date.toString()).format('DD-MM-YYYY');

  const options = calculateOptions(sortedCategories, twrField);

  return (
    <Stack gap={2} height={fullHeight ? '100%' : undefined}>
      {!hideTitle && <Typography level="body-xs">Last day: {dateSubTitle}</Typography>}
      <Suspense fallback={<Skeleton />}>
        <HighChartsWrapper
          height={fullHeight ? 'fullHeight' : undefined}
          loading={false}
          data={data}
          calculateChartData={(data) =>
            calculateChartData({
              colorScheme,
              data,
              categories: sortedCategories,
              twrField,
            })
          }
          calculateOptions={(): Highcharts.Options => options}
        />
      </Suspense>
    </Stack>
  );
};
